import React from "react";
import Table from "@mui/material/Table";
import TableRow from "@mui/material/TableRow";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import ColourConstants from "helpers/colourConstants";
import { makeStyles } from "tss-react/mui";
import TableStyle from "styles/application/TableStyle";
import { tableFeatures, useTable } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import useInfiniteScrollSentinel, { scrollTableToTop } from "hooks/useInfiniteScrollSentinel";
import AutoFitContentInScreen from "components/Layouts/AutoFitContentInScreen";

const AT = TableStyle();

// Pilot of the TanStack Table v9 headless architecture: the table owns models,
// this component owns the EMI markup/styles. Row models beyond core are
// server-side (sorting, paging), so no extra features are registered.
const features = tableFeatures({});

const EMPTY_DATA = [];

const useStyles = makeStyles()(() => ({
	tableBody: {
		whiteSpace: "noWrap",
	},
	table: {
		borderStyle: "solid",
		fontFamily: "Roboto Condensed",
		fontSize: 14,
		overflowX: "auto",
		borderColor: ColourConstants.tableBorder,
		borderWidth: 1,
		borderRadius: 0,
	},
	tableHeadRow: {
		borderBottomColor: ColourConstants.tableBorder,
		borderBottomStyle: "solid",
		borderBottomWidth: 1,
		backgroundColor: ColourConstants.tableBackground,
		fontWeight: "bold",
		borderRightColor: "#979797",
		borderRightStyle: "solid",
		borderRightWidth: "1px",
	},
	selectedTableHeadRow: {
		borderBottomColor: ColourConstants.tableBorder,
		borderBottomStyle: "solid",
		borderBottomWidth: 1,
		backgroundColor: ColourConstants.tableBackgroundSelected,
		fontWeight: "bold",
		color: "#FFFFFF",
	},
	headerRow: {
		height: "10px",
		lineHeight: "1rem",
	},
}));

const AuditHistoryTable = ({
	columns,
	rows,
	sortOrder,
	onToggleSort,
	hasNextPage,
	isFetchingNextPage,
	fetchNextPage,
	isFetchNextPageError,
}) => {
	const { classes, cx } = useStyles();
	const { t } = useTranslation();

	const table = useTable({ features, columns, data: rows ?? EMPTY_DATA });

	const { sentinelRef } = useInfiniteScrollSentinel({
		hasNextPage,
		isFetchingNextPage,
		fetchNextPage,
		isError: isFetchNextPageError,
	});

	return (
		<AutoFitContentInScreen containsTable={true}>
			<Table aria-label={t("title.auditHistory")} className={classes.table}>
				<AT.TableHead>
					{table.getHeaderGroups().map((group) => (
						<TableRow key={group.id}>
							{group.headers.map((header) => {
								const sortable = header.column.columnDef.meta?.sortable === true;
								return (
									<TableCell
										key={header.id}
										onClick={() => sortable && onToggleSort()}
										style={{ minWidth: header.column.columnDef.meta?.minWidth || "auto" }}
										className={cx(classes.headerRow, {
											[classes.selectedTableHeadRow]: sortable,
											[classes.tableHeadRow]: !sortable,
										})}
									>
										<AT.CellContainer className="flex justify-between">
											{header.isPlaceholder ? null : <table.FlexRender header={header} />}
											{sortable && (
												<div className="arrow">
													<AT.DescArrow
														fill={sortOrder === "asc" ? "#D2D2D9" : "#F9F9FC"}
														className="arrowUp"
													/>
													<AT.DefaultArrow
														fill={sortOrder === "desc" ? "#D2D2D9" : "#F9F9FC"}
														className="arrowDown"
													/>
												</div>
											)}
										</AT.CellContainer>
									</TableCell>
								);
							})}
						</TableRow>
					))}
				</AT.TableHead>
				<TableBody className={classes.tableBody}>
					{table.getRowModel().rows.map((row) => (
						<TableRow key={row.original.id}>
							{row.getAllCells().map((cell) => (
								<TableCell
									key={cell.id}
									style={{ maxWidth: "500px", whiteSpace: "normal" }}
								>
									<AT.CellContainer>
										<table.FlexRender cell={cell} />
									</AT.CellContainer>
								</TableCell>
							))}
						</TableRow>
					))}
				</TableBody>
			</Table>
			{hasNextPage && (
				<div
					ref={sentinelRef}
					data-testid="infinite-scroll-sentinel"
					style={{ padding: "16px 10px" }}
				>
					{isFetchingNextPage && <b>{t("message.loading")}</b>}
				</div>
			)}
			{!hasNextPage && (rows?.length ?? 0) > 0 && (
				<div
					style={{ textAlign: "center", padding: "16px 10px" }}
					className="flex justify-center"
				>
					<span
						className="link-color ml-md cursor-pointer"
						onClick={() => scrollTableToTop()}
					>
						{t("action.goToTop")}
					</span>
				</div>
			)}
		</AutoFitContentInScreen>
	);
};

export default AuditHistoryTable;
